<template>
  <div class="flex flex-col">
    <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
      <p class="text-sm text-gray-600 flex-1 min-w-0">
        Manage the SIMPO API key used by the integration service.
      </p>
      <button type="button" class="btn shrink-0" :disabled="loading" @click="loadApiKey">
        <i class="fa-solid fa-rotate-right" :class="{ 'fa-spin': loading }"></i>
        <span>Refresh</span>
      </button>
    </div>

    <div v-if="loading && !loaded" class="w-full bg-gray-200 rounded-full h-1 overflow-hidden mb-4">
      <div class="bg-primary h-1 rounded-full animate-pulse" style="width: 100%"></div>
    </div>

    <form class="max-w-lg space-y-4" @submit.prevent="saveApiKey">
      <div>
        <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">API key</label>
        <div class="relative mt-1">
          <input
            v-model="form.apiKey"
            type="text"
            autocomplete="off"
            placeholder="Enter SIMPO API key"
            class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50"
            :disabled="loading || saving"
            required
          />
        </div>
      </div>
      <div class="flex flex-wrap gap-2 pt-1">
        <button type="submit" class="btn-primary" :disabled="loading || saving">
          <i v-if="saving" class="fa fa-spinner fa-spin"></i>
          <i v-else class="fa-solid fa-save"></i>
          <span>{{ keyConfigured ? "Update API key" : "Save API key" }}</span>
        </button>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useToast } from "@/composables";
import { extractErrorMessage, fetchSimpoApiKey, saveSimpoApiKey } from "./simpoApi";

const { successToast, errorToast, warningToast } = useToast();

const loading = ref(false);
const saving = ref(false);
const loaded = ref(false);
const keyConfigured = ref(false);
const form = ref({ apiKey: "" });

const loadApiKey = async () => {
  loading.value = true;
  try {
    const result = await fetchSimpoApiKey();
    keyConfigured.value = result.configured;
    form.value.apiKey = result.apiKey || "";
    loaded.value = true;
  } catch (error: unknown) {
    const err = error as { response?: { status?: number } };
    if (err.response?.status === 404) {
      keyConfigured.value = false;
      form.value.apiKey = "";
      loaded.value = true;
      return;
    }
    errorToast("Error", extractErrorMessage(error, "Could not load SIMPO API key"));
  } finally {
    loading.value = false;
  }
};

const saveApiKey = async () => {
  const apiKey = form.value.apiKey.trim();
  if (!apiKey) {
    warningToast("Validation", "Enter an API key");
    return;
  }
  saving.value = true;
  try {
    await saveSimpoApiKey({ apiKey });
    successToast("Success", "SIMPO API key saved");
    await loadApiKey();
  } catch (error: unknown) {
    errorToast("Error", extractErrorMessage(error, "Could not save SIMPO API key"));
  } finally {
    saving.value = false;
  }
};

onMounted(() => {
  loadApiKey();
});

defineExpose({ refresh: loadApiKey });
</script>
